import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { XCircle, ArrowLeft } from "lucide-react";

/**
 * Public landing page shown when the customer abandons or fails the Stripe
 * Checkout session (magic-link flow). Reads ?reason= from URL to show a
 * human-friendly explanation.
 */
const REASONS = {
  cancelled: "Cancelaste el pago antes de completarlo.",
  invalid_link: "Este enlace de pago no es válido o ya expiró.",
  not_found: "No encontramos la factura asociada a este enlace.",
  account_missing: "No encontramos la cuenta asociada a este enlace.",
  invalid_amount: "Esta factura no tiene un monto válido.",
  stripe_disabled: "Los pagos en línea no están disponibles en este momento.",
  stripe_error: "Hubo un problema técnico con el procesador de pagos.",
  session_lookup_failed: "No pudimos verificar tu sesión de pago.",
  not_paid: "El pago no se completó correctamente.",
};

export default function PayCancelled() {
  const [params] = useSearchParams();
  const reason = params.get("reason") || "cancelled";
  const explainer = REASONS[reason] || REASONS.cancelled;
  const isExpired = reason === "invalid_link";

  useEffect(() => {
    document.title = `Pago no completado · Velcor Energy`;
  }, []);

  return (
    <main
      className="min-h-screen grid place-items-center bg-[#fef9f7] px-5 py-12"
      data-testid="pay-cancelled-page"
    >
      <article className="bg-white rounded-3xl border border-rose-200 max-w-[520px] w-full p-8 md:p-12 text-center shadow-sm">
        <div className="mx-auto mb-6 grid place-items-center w-20 h-20 rounded-full bg-rose-50">
          <XCircle className="w-12 h-12 text-rose-600" strokeWidth={1.6} />
        </div>
        <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-rose-700 mb-2">
          Pago no completado
        </div>
        <h1
          className="display text-velcor-ink leading-tight mb-4"
          style={{ fontSize: "clamp(26px, 4vw, 36px)", fontWeight: 600, letterSpacing: "-0.02em" }}
        >
          {isExpired ? "Este enlace ya expiró." : "No se completó el pago."}
        </h1>
        <p className="text-slate-500 text-[14.5px] leading-relaxed mb-7">
          {explainer}{" "}
          {isExpired
            ? "Por seguridad, los enlaces de pago caducan a los 14 días. Inicia sesión en tu portal para ver y pagar tus facturas."
            : "No se realizó ningún cargo a tu tarjeta. Puedes intentarlo de nuevo desde tu portal Velcor o desde el correo original."}
        </p>

        <div className="border-t border-slate-100 pt-7 flex flex-col gap-3">
          <Link
            to="/portal/login"
            data-testid="pay-cancel-portal-link"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-velcor-ink text-white font-bold text-[14px] hover:bg-velcor-ink/90 transition-colors"
          >
            Iniciar sesión y pagar <ArrowLeft className="w-4 h-4 rotate-180" />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 text-slate-500 hover:text-velcor-ink text-[13px] transition-colors"
          >
            Contactar a soporte
          </Link>
        </div>

        <p className="mt-9 text-[11px] text-slate-400 leading-relaxed">
          Velcor Energy, LLC · PUCT REP Certificate #VEL-2026 · (800) 715-6516
        </p>
      </article>
    </main>
  );
}
